import React, { useState } from 'react';
import api from '../../api';
import type { PromotionBriefing } from './promotionTypes';

interface PromotionBriefingEditorProps {
  briefing: PromotionBriefing;
  onChange: (briefing: PromotionBriefing) => void;
  auth: () => Promise<{ headers: { Authorization: string } }>;
  editable: boolean;
}

const PromotionBriefingEditor: React.FC<PromotionBriefingEditorProps> = ({
  briefing,
  onChange,
  auth,
  editable,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadImage = async (file?: File) => {
    if (!file || !editable) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setError('Choose a JPEG or PNG image.');
      return;
    }
    if (file.size === 0 || file.size > 5 * 1024 * 1024) {
      setError('Choose an image between 1 byte and 5 MB.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = new FormData();
      data.append('file', file);
      const result = await api.post<{ url: string }>(
        '/promotion-stats/promotions/card-media',
        data,
        await auth(),
      );
      if (!result.data.url.startsWith('https://')) {
        throw new Error('The uploaded image URL must use HTTPS.');
      }
      onChange({ ...briefing, imageUrl: result.data.url });
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : 'Could not upload this image.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='promotion-briefing-editor'>
      <div className='promotion-briefing-editor__fields'>
        <label>
          Headline
          <input
            aria-label='Headline'
            value={briefing.headline}
            disabled={!editable}
            onChange={(event) =>
              onChange({ ...briefing, headline: event.target.value })
            }
          />
          <span className='promotion-card-counter'>
            {briefing.headline.length} / 60
          </span>
        </label>
        <label>
          One line of copy
          <textarea
            aria-label='One line of copy'
            value={briefing.body}
            rows={3}
            disabled={!editable}
            onChange={(event) =>
              onChange({ ...briefing, body: event.target.value })
            }
          />
          <span className='promotion-card-counter'>
            {briefing.body.length} / 200
          </span>
        </label>
        <label>
          Image (optional, JPEG/PNG up to 5 MB)
          <input
            type='file'
            accept='image/jpeg,image/png'
            disabled={!editable || busy}
            onChange={(event) => void uploadImage(event.target.files?.[0])}
          />
        </label>
        {briefing.imageUrl && (
          <div className='promotion-briefing-image'>
            <img src={briefing.imageUrl} alt='' />
            <button
              type='button'
              className='launch-btn launch-btn--quiet'
              disabled={!editable}
              onClick={() => {
                onChange({ ...briefing, imageUrl: null });
                setError(null);
              }}
            >
              Remove
            </button>
          </div>
        )}
        {busy && <p className='launch-hint'>Uploading image…</p>}
        {error && (
          <p className='promotion-briefing-editor__error' role='alert'>
            {error}
          </p>
        )}
      </div>
      <div className='promotion-briefing-preview' aria-label='Card preview'>
        {briefing.imageUrl && (
          <img
            className='promotion-briefing-preview__image'
            src={briefing.imageUrl}
            alt=''
          />
        )}
        <div className='promotion-briefing-preview__copy'>
          <strong>{briefing.headline || 'Your headline'}</strong>
          <p>{briefing.body || 'Your card copy will appear here.'}</p>
        </div>
        <div className='promotion-briefing-preview__actions'>
          <button type='button' disabled>
            Tell me more about this
          </button>
          <button type='button' disabled>
            Not for me
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromotionBriefingEditor;
